import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Chip,
  Card,
  CardBody,
  Divider,
  Link,
} from "@heroui/react";
import {
  Eye,
  FileText,
  Calendar,
  Tag,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import { DocumentTemplate, DocumentCategory } from "./types";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";

interface PreviewDocumentTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: DocumentTemplate | null;
}

const PreviewDocumentTemplateModal: React.FC<
  PreviewDocumentTemplateModalProps
> = ({ isOpen, onClose, template }) => {
  const { session } = useSession();
  const [sharePointUrl, setSharePointUrl] = useState<string | null>(null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);

  // Fetch SharePoint URL when template changes
  useEffect(() => {
    if (template?.sharePointTemplateId && isOpen) {
      fetchSharePointUrl(template.sharePointTemplateId);
    }
  }, [template?.sharePointTemplateId, isOpen]);

  const fetchSharePointUrl = async (fileId: string) => {
    try {
      setIsLoadingUrl(true);
      const response = await fetchWithAuth(
        `${API_URL}/graph/file/${fileId}/web-url`,
        { method: "GET" },
        session,
      );

      if (response.ok) {
        const data = await response.json();
        setSharePointUrl(data.webUrl);
      }
    } catch (error) {
      console.error("Error fetching SharePoint URL:", error);
    } finally {
      setIsLoadingUrl(false);
    }
  };

  if (!template) return null;

  const getCategoryLabel = (category: DocumentCategory) => {
    switch (category) {
      case DocumentCategory.ACQUISITION:
        return "Acquisitie";
      case DocumentCategory.CONTRACT:
        return "Contract";
      case DocumentCategory.INSPECTION:
        return "Inspectie";
      case DocumentCategory.LEGAL:
        return "Juridisch";
      case DocumentCategory.RENOVATION:
        return "Renovatie";
      case DocumentCategory.GENERAL:
        return "Algemeen";
      default:
        return category;
    }
  };

  const getCategoryColor = (category: DocumentCategory) => {
    switch (category) {
      case DocumentCategory.ACQUISITION:
        return "primary";
      case DocumentCategory.CONTRACT:
        return "success";
      case DocumentCategory.INSPECTION:
        return "warning";
      case DocumentCategory.LEGAL:
        return "danger";
      case DocumentCategory.RENOVATION:
        return "secondary";
      case DocumentCategory.GENERAL:
        return "default";
      default:
        return "default";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("nl-BE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader>
          <div className="flex items-center gap-2">
            <Eye size={20} />
            Document Sjabloon Details
          </div>
        </ModalHeader>
        <ModalBody className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardBody className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-neutral-primary text-lg font-semibold">
                    {template.name}
                  </h3>
                  <p className="text-neutral-secondaryMid text-sm">
                    {template.templateKey}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Chip
                    color={getCategoryColor(template.category)}
                    variant="flat"
                  >
                    {getCategoryLabel(template.category)}
                  </Chip>
                </div>
              </div>

              {template.description && (
                <div>
                  <p className="text-neutral-primary text-sm">
                    {template.description}
                  </p>
                </div>
              )}

              <Divider />

              <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-neutral-secondaryMid" />
                  <span className="text-neutral-secondaryMid">SharePoint:</span>
                  <div className="flex items-center gap-2">
                    {isLoadingUrl ? (
                      <span className="text-neutral-secondaryMid text-sm">
                        Laden...
                      </span>
                    ) : sharePointUrl ? (
                      <Link
                        href={sharePointUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary-600 transition-colors"
                        size="sm"
                      >
                        <div className="flex items-center gap-1">
                          <span>Bekijk bestand</span>
                          <ArrowSquareOut size={14} />
                        </div>
                      </Link>
                    ) : (
                      <span className="text-neutral-secondaryMid text-sm">
                        URL niet beschikbaar
                      </span>
                    )}
                    <code className="text-neutral-secondaryMid rounded bg-neutral-100 px-2 py-1 text-xs">
                      {template.sharePointTemplateId.substring(0, 8)}...
                    </code>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-neutral-secondaryMid" />
                  <span className="text-neutral-secondaryMid">Aangemaakt:</span>
                  <span>{formatDate(template.createdAt)}</span>
                </div>
              </div>

              {template.legacyEndpoint && (
                <div className="flex items-center gap-2 text-sm">
                  <Tag size={16} className="text-neutral-secondaryMid" />
                  <span className="text-neutral-secondaryMid">
                    Legacy Endpoint:
                  </span>
                  <code className="rounded bg-neutral-100 px-2 py-1 text-xs">
                    {template.legacyEndpoint}
                  </code>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Required Context */}
          <Card>
            <CardBody>
              <h4 className="text-neutral-primary mb-3 font-semibold">
                Vereiste Context
              </h4>
              <p className="text-neutral-secondaryMid mb-3 text-sm">
                Deze gegevens zijn nodig om het document te kunnen genereren:
              </p>
              <div className="flex flex-wrap gap-2">
                {template.requiredContext.map((context) => (
                  <Chip key={context} variant="dot" color="secondary">
                    {context}
                  </Chip>
                ))}
              </div>
              {template.requiredContext.length === 0 && (
                <p className="text-neutral-secondaryMid text-sm italic">
                  Geen specifieke context vereist
                </p>
              )}
            </CardBody>
          </Card>

          {/* Available Placeholders */}
          <Card>
            <CardBody>
              <h4 className="text-neutral-primary mb-3 font-semibold">
                Beschikbare Placeholders (
                {template.availablePlaceholders.length})
              </h4>
              <p className="text-neutral-secondaryMid mb-3 text-sm">
                Deze placeholders kunnen worden gebruikt in het Word document:
              </p>
              <div className="flex flex-wrap gap-2">
                {template.availablePlaceholders.map((placeholder) => (
                  <Chip
                    key={placeholder}
                    variant="bordered"
                    color="primary"
                    size="sm"
                  >
                    {placeholder}
                  </Chip>
                ))}
              </div>
              {template.availablePlaceholders.length === 0 && (
                <p className="text-neutral-secondaryMid text-sm italic">
                  Geen placeholders gedefinieerd
                </p>
              )}
            </CardBody>
          </Card>

          {/* Metadata */}
          {template.metadata && Object.keys(template.metadata).length > 0 && (
            <Card>
              <CardBody>
                <h4 className="text-neutral-primary mb-3 font-semibold">
                  Metadata
                </h4>
                <div className="rounded-lg bg-neutral-50 p-3">
                  <pre className="text-neutral-primary overflow-x-auto text-xs">
                    {JSON.stringify(template.metadata, null, 2)}
                  </pre>
                </div>
              </CardBody>
            </Card>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onPress={onClose}>Sluiten</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PreviewDocumentTemplateModal;
